<template>
  <Report :product="product" :isDetailsVisible="!isLoggedIn" :show-secondary-info="isRelatedCompaniesTabOpen" :show-share-info="isShareTabOpen">
    <template v-slot:callToAction>
      <BuyReport
        :product="product"
        :scope="product.id === 'share-my-payment-defaults' ? 'report.share' : ''"
        @buy="buy"
        :class="[isRelatedCompaniesTabOpen ? 'd-none' : 'd-none d-md-block', isShareTabOpen ? 'share-report-anim' : '']"
        :hasCallToActionButton="product.id === 'share-my-payment-defaults'"
      />
    </template>
    <template v-slot:default>
      <BuyReport
        :product="product"
        :scope="product.id === 'share-my-payment-defaults' ? 'report.share' : ''"
        @buy="buy"
        :class="[isRelatedCompaniesTabOpen ? 'd-none' : 'd-md-none', isShareTabOpen ? 'share-report-anim' : '']"
        :hasCallToActionButton="product.id === 'share-my-payment-defaults'"
      />
      <BTabs nav-class="small-tabs d-print-none" content-class="structure-tabs" v-model="activeTab" v-if="!isShareTabOpen">
        <BTab :id="0" :title="$t('report.payment')" @click="tabClick('my-payment-defaults')">
          <div class="tab-container-medium d-print-none" v-if="paymentDefaults">
            <div v-if="!paymentDefaults.isSample && !paymentDefaultsAreEmpty()" class="row float-right">
              <router-link
                class="icon-double-arrow-right fw-bold font-size-larger"
                :to="{ name: `disputeInfo-${this.$i18n.locale}`, params: { lang: this.$i18n.locale}, state: {isPrivate: true } }">
                {{ $t('report.dispute') }}
              </router-link>
            </div>
            <Watermark :text="product.watermarkLabel" v-if="paymentDefaults.isSample" />
            <div v-for="group in paymentDefaultsGroups" :key="group">
              <span class="tab-subheading">{{ $t(`report.paymentDefaults.${group}`) }}</span>
              <table class="table structure-table" v-if="paymentDefaults[group].length">
                <thead>
                  <tr>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.institution') }}
                    </th>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.startDate') }}
                    </th>
                    <template v-if="hasEndDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.endDate') }}
                      </th>
                    </template>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.finalSize') }}
                    </th>
                    <template v-if="hasMaxSum(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.maxSize') }}
                      </th>
                    </template>
                    <template v-if="hasDisputeDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.disputeDate') }}
                      </th>
                    </template>
                    <template v-if="hasLiquidationDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.liquidationDate') }}
                      </th>
                    </template>
                    <template v-if="hasPublishInfo(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.published') }}
                      </th>
                    </template>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.input') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in paymentDefaults[group]" :key="index">
                    <td>
                      <span class="td-header d-block d-xl-none">
                        {{ $t('report.paymentDefaults.labels.institution') }}
                      </span>
                      {{ item.creditor }}
                    </td>
                    <td>
                      <span class="td-header d-block d-xl-none">
                        {{ $t('report.paymentDefaults.labels.startDate') }}
                      </span>
                      {{ $date(item.startDate) }}
                    </td>
                    <template v-if="hasEndDate(group)">
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.endDate') }}
                        </span>
                        <template v-if="item.endDate">
                          {{ $date(item.endDate) }}
                        </template>
                        <div class="marker" v-else>
                          {{ $t('report.paymentDefaults.labels.outstanding') }}
                        </div>
                      </td>
                    </template>
                    <td class="text-end">
                      <span class="td-header d-block d-xl-none">
                        {{ $t('report.paymentDefaults.labels.finalSize') }}
                      </span>
                      <template v-if="item.finalSize">
                        {{ formatAmount(item.finalSize) }}
                      </template>
                      <template v-else>‒</template>
                    </td>
                    <template v-if="hasMaxSum(group)">
                      <td class="text-end">
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.maxSize') }}
                        </span>
                        <template v-if="item.maxSize">
                          {{ formatAmount(item.maxSize) }}
                        </template>
                        <template v-else>‒</template>
                      </td>
                    </template>
                    <template v-if="hasDisputeDate(group)">
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.disputeDate') }}
                        </span>
                        {{ $date(item.disputeDate) }}
                      </td>
                    </template>
                    <template v-if="hasLiquidationDate(group)">
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.liquidationDate') }}
                        </span>
                        {{ $date(item.liquidationDate) }}
                      </td>
                    </template>
                    <template v-if="hasPublishInfo(group)">
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.published') }}
                        </span>
                        {{ item.published }}
                      </td>
                    </template>
                    <td>
                      <span class="td-header d-block d-xl-none">
                        {{ $t('report.paymentDefaults.labels.input') }}
                      </span>
                      {{ item.field }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="alert alert-default mb-7 mt-5" role="alert" v-else>
                {{ $t(`report.paymentDefaults.${group}Empty`) }}
              </div>
            </div>
            <p class="table-caption" x-ms-format-detection="none">
              <span class="icon-info2 marker" />&nbsp;
              <span v-html="$t('report.paymentDefaults.hint')" />
            </p>
          </div>
          <Spinner v-else />
        </BTab>
        <BTab :id="1" :title="$t('report.share.title')" @click="tabClick('share-my-payment-defaults')">
          <div class="tab-container-medium" v-html="product.share" />
        </BTab>
        <BTab :id="2" :title="$t('report.companyPayment')" @click="loadCompanyPaymentDefaults()">
          <div class="tab-container-medium d-print-none" v-if="companyPaymentDefaults">
            <div class="row float-end" v-if="!companyPaymentDefaults.isSample">
              <router-link
                class="icon-double-arrow-right fw-bold font-size-larger"
                :to="{ name: `disputeInfo-${this.$i18n.locale}`, params: { lang: this.$i18n.locale }, state: { isPrivate: false } }">
                {{ $t('report.dispute') }}
              </router-link>
            </div>
            <Watermark :text="product.watermarkLabel" v-if="companyPaymentDefaults.isSample" />
            <div v-for="group in paymentDefaultsGroups" :key="`c-${group}`">
              <span class="tab-subheading">{{ $t(`report.paymentDefaults.${group}`) }}</span>
              <table class="table structure-table" v-if="Object.keys(companyPaymentDefaults[group]).length !== 0">
                <thead>
                  <tr>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.company') }}
                    </th>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.institution') }}
                    </th>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.startDate') }}
                    </th>
                    <template v-if="hasEndDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.endDate') }}
                      </th>
                    </template>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.finalSize') }}
                    </th>
                    <template v-if="hasMaxSum(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.maxSize') }}
                      </th>
                    </template>
                    <template v-if="hasDisputeDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.disputeDate') }}
                      </th>
                    </template>
                    <template v-if="hasLiquidationDate(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.liquidationDate') }}
                      </th>
                    </template>
                    <template v-if="hasPublishInfo(group)">
                      <th class="d-none d-xl-table-cell">
                        {{ $t('report.paymentDefaults.labels.published') }}
                      </th>
                    </template>
                    <th class="d-none d-xl-table-cell">
                      {{ $t('report.paymentDefaults.labels.input') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(pds, company, idx) in companyPaymentDefaults[group]">
                    <tr v-for="(item, companyIndex) in pds" :class="idx === (Object.keys(companyPaymentDefaults[group]).length - 1) ? '' : 'company-border-bottom' ">
                      <td
                        v-if="companyIndex === 0"
                        :rowspan="Object.keys(pds).length"
                        class="company-col">
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.company') }}
                        </span>
                        {{ company }}
                      </td>
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.institution') }}
                        </span>
                        {{ item.creditor }}
                      </td>
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.startDate') }}
                        </span>
                        {{ $date(item.startDate) }}
                      </td>
                      <template v-if="hasEndDate(group)">
                        <td>
                          <span class="td-header d-block d-xl-none">
                            {{ $t('report.paymentDefaults.labels.endDate') }}
                          </span>
                          <template v-if="item.endDate">
                            {{ $date(item.endDate) }}
                          </template>
                          <div class="marker" v-else>
                            {{ $t('report.paymentDefaults.labels.outstanding') }}
                          </div>
                        </td>
                      </template>
                      <td class="text-end">
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.finalSize') }}
                        </span>
                        <template v-if="item.finalSize">
                          {{ formatAmount(item.finalSize) }}
                        </template>
                        <template v-else>‒</template>
                      </td>
                      <template v-if="hasMaxSum(group)">
                        <td class="text-end">
                          <span class="td-header d-block d-xl-none">
                            {{ $t('report.paymentDefaults.labels.maxSize') }}
                          </span>
                          <template v-if="item.maxSize">
                            {{ formatAmount(item.maxSize) }}
                          </template>
                          <template v-else>‒</template>
                        </td>
                      </template>
                      <template v-if="hasDisputeDate(group)">
                        <td>
                          <span class="td-header d-block d-xl-none">
                            {{ $t('report.paymentDefaults.labels.disputeDate') }}
                          </span>
                          {{ $date(item.disputeDate) }}
                        </td>
                      </template>
                      <template v-if="hasLiquidationDate(group)">
                        <td>
                          <span class="td-header d-block d-xl-none">
                            {{ $t('report.paymentDefaults.labels.liquidationDate') }}
                          </span>
                          {{ $date(item.liquidationDate) }}
                        </td>
                      </template>
                      <template v-if="hasPublishInfo(group)">
                        <td>
                          <span class="td-header d-block d-xl-none">
                            {{ $t('report.paymentDefaults.labels.published') }}
                          </span>
                          {{ item.published }}
                        </td>
                      </template>
                      <td>
                        <span class="td-header d-block d-xl-none">
                          {{ $t('report.paymentDefaults.labels.input') }}
                        </span>
                        {{ item.field }}
                      </td>
                    </tr>

                  </template>
                </tbody>
              </table>
              <div class="alert alert-default mb-7 mt-5" role="alert" v-else>
                {{ $t(`report.paymentDefaults.${group}Empty`) }}
              </div>
            </div>
            <p class="table-caption" x-ms-format-detection="none">
              <span class="icon-info2 marker" />&nbsp;
              <span v-html="$t('report.paymentDefaults.hint')" />
            </p>
          </div>
          <Spinner v-else />
        </BTab>
      </BTabs>
    </template>
  </Report>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Report from './Report';
import BuyReport from './BuyReport';
import ExpandableList from './ExpandableList';
import Watermark from './Watermark';
import Spinner from './Spinner';
import { BTab, BTabs } from 'bootstrap-vue-next';

export default {
  props: {
    product: { type: Object, required: true },
    tab: { type: [Number, String], default: 0 },
  },
  components: {
    BTab,
    BTabs,
    Report,
    BuyReport,
    ExpandableList,
    Watermark,
    Spinner,
  },
  data() {
    return {
      activeTab: this.tab,
      paymentDefaults: null,
      companyPaymentDefaults: null,
      paymentDefaultsGroups: [
        'waitingPaymentDefaults',
        'waitingDisputedPaymentDefaults',
        'disputedPaymentDefaults',
        'activePaymentDefaults',
        'endedPaymentDefaults',
      ],
      groupsWithDisputeDate: [
        'waitingDisputedPaymentDefaults',
        'disputedPaymentDefaults',
      ],
      groupsWithLiquidationDate: [
        'waitingPaymentDefaults',
        'waitingDisputedPaymentDefaults',
      ],
      groupsWithPublishInfo: [
        'disputedPaymentDefaults',
      ],
      groupsWithMaxSum: [
        'disputedPaymentDefaults',
        'activePaymentDefaults',
        'endedPaymentDefaults',
      ],
      groupsWithEndDate: [
        'waitingPaymentDefaults',
        'waitingDisputedPaymentDefaults',
        'disputedPaymentDefaults',
        'activePaymentDefaults',
        'endedPaymentDefaults',
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['isLoggedIn']),

    isShareTabOpen() {
      return +this.activeTab === 1;
    },
    isRelatedCompaniesTabOpen() {
      return +this.activeTab === 2;
    },
  },
  methods: {
    ...mapActions('product', ['getMyPaymentDefaults', 'getRelatedCompanyPaymentDefaults', 'getProductById']),
    formatAmount(value) {
      const num = Number(value);
      if (!isFinite(num)) return '';
      const fixed = num.toFixed(2);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join('.');
    },
    buy() {
      this.$emit('buy');
    },
    loadCompanyPaymentDefaults() {
      this.getRelatedCompanyPaymentDefaults()
        .then((details) => {
          this.companyPaymentDefaults = details;
        });
    },
    loadPaymentDefaults() {
      this.getMyPaymentDefaults()
        .then((details) => this.paymentDefaults = details);
    },
    tabClick(id) {
      this.getProductById(id).then((product) => {
        this.$router.push({ name: `report-${this.$i18n.locale}`, params: { ...product, lang: this.$i18n.locale } });
      });
    },
    hasDisputeDate(group) {
      const { length } = this.groupsWithDisputeDate;
      for (let i = 0; i < length; i++) {
        if (this.groupsWithDisputeDate[i] === group) return true;
      }
      return false;
    },
    hasLiquidationDate(group) {
      const { length } = this.groupsWithLiquidationDate;
      for (let i = 0; i < length; i++) {
        if (this.groupsWithLiquidationDate[i] === group) return true;
      }
      return false;
    },
    hasPublishInfo(group) {
      const { length } = this.groupsWithPublishInfo;
      for (let i = 0; i < length; i++) {
        if (this.groupsWithPublishInfo[i] === group) return true;
      }
      return false;
    },
    hasEndDate(group) {
      const { length } = this.groupsWithEndDate;
      for (let i = 0; i < length; i++) {
        if (this.groupsWithEndDate[i] === group) return true;
      }
      return false;
    },
    hasMaxSum(group) {
      const { length } = this.groupsWithMaxSum;
      for (let i = 0; i < length; i++) {
        if (this.groupsWithMaxSum[i] === group) return true;
      }
      return false;
    },
    paymentDefaultsAreEmpty() {
      for (const val of Object.values(this.paymentDefaults)) {
        if (val.length > 0) return false;
      }
      return true;
    },
  },
  created() {
    this.loadPaymentDefaults();
  },
};
</script>
<style scoped>
  .company-col {
    background-color: #f1f1f1;
  }
  .company-border-bottom td{
    border-bottom: 1px solid #e3e2e2;
  }
</style>
